import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  CheckCircle,
  Download,
  Upload,
  Clock,
} from 'lucide-react';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import SEO from '@/components/shared/SEO';
import { cn } from '@/lib/utils';
import {
  servicePillars,
  featuredInstallations,
  processSteps,
  industries,
  resources,
  whyPorticoPoints,
} from '@/data/customHubData';

const CustomHubPage = () => {
  const [formData, setFormData] = useState({
    company: '',
    projectType: '',
    timeline: '',
    squareFootage: '',
    contactName: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`/api/custom-projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.contactName,
          email: formData.email,
          phone: formData.phone,
          company: formData.companyName,
          projectDescription: `Project Type: ${formData.projectType}\n${formData.message}`,
          quantity: formData.squareFootage,
          timeline: formData.timeline,
        }),
      });
      if (!res.ok) throw new Error('Submit failed');
      alert('Thank you for your inquiry! Our team will contact you within 24 hours.');
    } catch (err) {
      console.error('Custom project submission error:', err);
      alert("We couldn't submit your inquiry. Please try again, or email custom@porticosystems.com directly.");
    }
  };

  return (
    <Layout>
      <SEO 
        title="Custom Flooring Solutions"
        description="Create custom branded environments with Portico's custom logo mats, flooring solutions, and large-scale installation services. Transform your space with personalized commercial flooring."
        canonicalUrl="/custom"
      />
      {/* HERO SECTION - Authority Positioning */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden" data-testid="custom-hero">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src="/Assets/images/industries/corporate-offices.jpg"
            alt="Custom Branded Environments"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-bg/95 via-primary-bg/85 to-primary-bg/75" />
        </div>
        
        {/* Hero Content */}
        <div className="container-portico relative z-10 text-center text-white py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Custom Branded Environments
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-10 font-light">
              From Logo Mats to Full-Scope Installations
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-secondary hover:bg-secondary-light text-white px-8 py-6 text-lg"
                onClick={() => document.getElementById('consultation-form').scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-cta-consultation"
              >
                Request Consultation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Link to="/custom/portfolio">
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-white text-white hover:bg-white hover:text-primary px-8 py-6 text-lg"
                  data-testid="hero-cta-portfolio"
                >
                  View Portfolio
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHAT WE DO - Service Pillars */}
      <section className="section-padding bg-background" data-testid="service-pillars">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              What We Do
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Comprehensive custom solutions designed to elevate your brand and protect your investment.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {servicePillars.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link 
                  to={service.href}
                  className="group block h-full"
                  data-testid={`service-card-${service.id}`}
                >
                  <div className="relative h-full bg-card rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-border hover:border-primary">
                    {/* Image */}
                    <div className="h-48 overflow-hidden">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    
                    {/* Content */}
                    <div className="p-6">
                      <h3 className="font-heading font-semibold text-lg mb-2 group-hover:text-primary transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        {service.description}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {service.features.map((feature) => (
                          <span 
                            key={feature} 
                            className="text-xs px-2 py-1 bg-muted rounded-full text-muted-foreground"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                      <div className="mt-4 flex items-center text-sm font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                        Learn More <ArrowRight className="ml-1 w-4 h-4" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED INSTALLATIONS - Case Study Style */}
      <section className="section-padding bg-muted" data-testid="featured-installations">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Featured Installations
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Explore our portfolio of successful custom branded environment projects.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {featuredInstallations.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link 
                  to={project.href}
                  className="group block"
                  data-testid={`portfolio-card-${project.id}`}
                >
                  <div className="bg-card rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
                    {/* Large Image */}
                    <div className="h-64 overflow-hidden">
                      <img
                        src={project.image}
                        alt={project.projectName}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    
                    {/* Content */}
                    <div className="p-6">
                      <span className="text-xs font-semibold text-secondary uppercase tracking-wider">
                        {project.industry}
                      </span>
                      <h3 className="font-heading font-bold text-xl mt-2 mb-3 group-hover:text-primary transition-colors">
                        {project.projectName}
                      </h3>
                      <p className="text-muted-foreground text-sm mb-4">
                        {project.scope}
                      </p>
                      <div className="flex items-center text-sm font-semibold text-primary">
                        View Project <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/custom/portfolio">
              <Button size="lg" variant="outline" data-testid="view-all-portfolio">
                View Full Portfolio
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* PROCESS SECTION - Trust Builder */}
      <section className="section-padding bg-background" data-testid="process-section">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Our Process
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              A streamlined approach to delivering exceptional custom solutions.
            </p>
          </motion.div>

          {/* Desktop Horizontal Timeline */}
          <div className="hidden lg:block">
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute top-16 left-0 right-0 h-0.5 bg-border" />
              
              <div className="grid grid-cols-5 gap-4">
                {processSteps.map((step, index) => (
                  <motion.div
                    key={step.step}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="text-center"
                  >
                    {/* Step Circle */}
                    <div className="relative inline-flex items-center justify-center w-32 h-32 rounded-full bg-primary text-white mb-6 mx-auto shadow-lg">
                      <div className="text-center">
                        <span className="block text-3xl font-bold">{step.step}</span>
                      </div>
                    </div>
                    
                    <h3 className="font-heading font-semibold text-lg mb-2">
                      {step.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {step.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Mobile Vertical Stack */}
          <div className="lg:hidden space-y-6">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-4"
              >
                <div className="flex-shrink-0 w-16 h-16 rounded-full bg-primary text-white flex items-center justify-center font-bold text-xl">
                  {step.step}
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-lg mb-1">
                    {step.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES SERVED */}
      <section className="section-padding bg-muted" data-testid="industries-section">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Industries We Serve
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Specialized solutions tailored to the unique requirements of your industry.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {industries.map((industry, index) => (
              <motion.div
                key={industry.name}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="h-full"
              >
                <Link
                  to={industry.href}
                  className="group flex flex-col items-center justify-center text-center h-full min-h-[180px] p-6 bg-card rounded-xl hover:bg-primary hover:text-white transition-all duration-300 border border-border hover:border-primary shadow-sm hover:shadow-lg"
                  data-testid={`industry-${industry.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <industry.icon className="w-10 h-10 mb-3 text-primary group-hover:text-white transition-colors" />
                  <h3 className="font-heading font-semibold">
                    {industry.name}
                  </h3>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY PORTICO CUSTOM - Authority Section */}
      <section className="section-padding bg-primary-bg text-white" data-testid="why-portico-section">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Why Portico Custom?
            </h2>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              Partner with the industry leader in custom branded environments.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {whyPorticoPoints.map((point, index) => (
              <motion.div
                key={point.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-secondary" />
                </div>
                <h3 className="font-heading font-semibold text-lg mb-2">
                  {point.title}
                </h3>
                <p className="text-sm text-white/70">
                  {point.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CONVERSION SECTION - Premium Consultation Form */}
      <section id="consultation-form" className="section-padding bg-muted" data-testid="consultation-section">
        <div className="container-portico">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Start Your Custom Project
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Tell us about your project and our team will provide a customized solution within 24 hours.
              </p>
            </motion.div>

            <motion.form
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              onSubmit={handleSubmit}
              className="bg-card rounded-2xl shadow-xl p-8 md:p-12 border border-border"
              data-testid="consultation-form-element"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Company */}
                <div>
                  <label className="block text-sm font-medium mb-2">Company Name *</label>
                  <Input
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder="Your company name"
                    required
                    className="h-12"
                  />
                </div>

                {/* Contact Name */}
                <div>
                  <label className="block text-sm font-medium mb-2">Contact Name *</label>
                  <Input
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleInputChange}
                    placeholder="Your full name"
                    required
                    className="h-12"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium mb-2">Email Address *</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="you@company.com"
                    required
                    className="h-12"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium mb-2">Phone Number</label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="(555) 123-4567"
                    className="h-12"
                  />
                </div>

                {/* Project Type */}
                <div>
                  <label className="block text-sm font-medium mb-2">Project Type *</label>
                  <select
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleInputChange}
                    required
                    className="w-full h-12 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="">Select project type</option>
                    <option value="logo-mats">Custom Logo Mats</option>
                    <option value="flooring">Custom Flooring Solutions</option>
                    <option value="installation">Large-Scale Installation</option>
                    <option value="consultation">Design Consultation</option>
                    <option value="multi-location">Multi-Location Rollout</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Timeline */}
                <div>
                  <label className="block text-sm font-medium mb-2">Project Timeline</label>
                  <select
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleInputChange}
                    className="w-full h-12 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="">Select timeline</option>
                    <option value="immediate">Immediate (Within 30 days)</option>
                    <option value="1-3-months">1-3 Months</option>
                    <option value="3-6-months">3-6 Months</option>
                    <option value="6-12-months">6-12 Months</option>
                    <option value="planning">Planning Phase</option>
                  </select>
                </div>

                {/* Square Footage */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Estimated Square Footage</label>
                  <Input
                    name="squareFootage"
                    value={formData.squareFootage}
                    onChange={handleInputChange}
                    placeholder="e.g., 10,000 sq ft or Multiple locations"
                    className="h-12"
                  />
                </div>
              </div>

              {/* Message */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Project Details</label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Tell us about your project requirements, goals, and any specific needs..."
                  rows={4}
                  className="resize-none"
                />
              </div>

              {/* File Upload Placeholder */}
              <div className="mb-8">
                <label className="block text-sm font-medium mb-2">Upload Artwork (Optional)</label>
                <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary transition-colors cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Drag & drop files here or click to browse
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Supports: AI, EPS, PDF, PNG, JPG (Max 25MB)
                  </p>
                </div>
              </div>

              {/* Submit Button */}
              <Button 
                type="submit" 
                size="lg" 
                className="w-full h-14 text-lg"
                data-testid="submit-inquiry-btn"
              >
                Submit Project Inquiry
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>

              <p className="text-xs text-muted-foreground text-center mt-4">
                By submitting this form, you agree to our Privacy Policy. We'll respond within 24 business hours.
              </p>
            </motion.form>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CustomHubPage;
